import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Vault,
  Building2,
  Wrench,
  ShieldCheck,
  HardDrive,
  History,
  Repeat,
  Pencil,
  FileBarChart,
  CalendarX2,
  ClipboardList,
  Trash2,
} from "lucide-react";
import * as devicesApi from "../../api/devices";
import {
  DeviceDettaglio,
  RelazioneCliente,
  STATO_LABELS,
  TIPO_POSSESSO_LABELS,
  modelloLeggibile,
  centroInstallazione,
} from "../../api/devices";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { useTornaIndietro } from "../../hooks/useStatoUrl";
import { LinkIndietro } from "../../components/ui/LinkIndietro";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { SECTION_COLORS, SectionKey } from "../../utils/sectionColors";
import { DataStorico } from "../../components/ui/DataStorico";
import { coloreStato } from "./Devices";
import { collegamentoCorrente } from "./collegamento";
import { DeviceModifica } from "./DeviceModifica";
import { TerminaRapporto } from "./TerminaRapporto";
import { EliminaRelazione } from "./EliminaRelazione";
import { InviaReportModal } from "../../components/ui/InviaReportModal";
import * as reportApi from "../../api/report";
import { etichettaMatricola } from "../../utils/matricola";

/**
 * I clienti serviti, in una riga sola. Sono un elenco e non uno: la cassaforte
 * e' condivisa quando piu' ragioni sociali versano sullo stesso apparecchio, e
 * quel fatto non va nascosto dietro il nome del primo che capita.
 */
function clientiInRiga(correnti: { cliente: { nome: string } }[]): string {
  if (correnti.length === 0) return "nessuna relazione in corso";
  if (correnti.length === 1) return correnti[0].cliente.nome;
  if (correnti.length === 2) return `${correnti[0].cliente.nome} e ${correnti[1].cliente.nome}`;
  return `${correnti[0].cliente.nome} e altri ${correnti.length - 1} clienti`;
}

function dataIT(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString("it-IT") : "—";
}

interface RigaStorico {
  id: string;
  titolo: string;
  data: string | null;
  /**
   * true se `data` viene dal campo della data PREVISTA e non di quella
   * avvenuta. Senza, la cascata `data_effettiva ?? ipotesi_data` butta via
   * proprio quello che uno storico deve dire — se la cosa è successa davvero.
   * Vedi `components/ui/DataStorico.tsx`.
   */
  dataStimata: boolean;
  /**
   * true solo per le installazioni: `ipotesi_data` è l'unica colonna in cui il
   * primo del mese è un segnaposto di mese e non un giorno. Vedi DataStorico.
   */
  daIpotesiData?: boolean;
  stato: string | null;
  dettaglio: string | null;
}

/**
 * I tipi di dispositivo che una comunicazione ce l'hanno. Sono gli stessi due
 * per cui il form Attivazioni mostra la sezione Comunicazione
 * (`DEVICE_TYPES_WITH_COMUNICAZIONE` in `routes/activations.ts`): un Rendiresto
 * o una TVCC non si collegano, e la card qui non ha senso.
 */
const TIPI_CON_COLLEGAMENTO = ["Cassaforte", "Allarme"];

/**
 * Scheda di una singola cassaforte: dove si trova oggi, dov'è stata, e cosa è
 * stato fatto su di lei.
 *
 * Le tre sezioni in fondo (installazioni, attivazioni, interventi) mostrano i
 * record che referenziano questo device ma NON sono cliccabili, come nella
 * Vista Sede: quei moduli aprono il dettaglio in un pannello laterale e non
 * hanno un URL per singolo record a cui puntare.
 */
export default function DeviceDetail() {
  const { id } = useParams<{ id: string }>();
  const torna = useTornaIndietro();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [device, setDevice] = useState<DeviceDettaglio | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modificaAperta, setModificaAperta] = useState(false);
  /** Pannello del Report dispositivo: scarico e invio. Niente periodo — la
      storia di un pezzo si legge intera. */
  const [reportAperto, setReportAperto] = useState(false);
  /**
   * La relazione di cui si sta chiudendo il rapporto, se se ne sta chiudendo
   * uno. Si tiene la riga intera e non il solo id perché il pannello mostra
   * cliente, sede e data di inizio: ripescarli dall'id costringerebbe a
   * cercare nell'elenco a ogni render.
   */
  const [daTerminare, setDaTerminare] = useState<RelazioneCliente | null>(null);
  /**
   * La relazione che si sta cancellando. Stato SEPARATO da `daTerminare` e non
   * una coppia (relazione, modo): sono due operazioni diverse con due esiti
   * diversi, e un solo stato con un interruttore renderebbe possibile aprire
   * il pannello sbagliato con un errore di una riga.
   */
  const [daEliminare, setDaEliminare] = useState<RelazioneCliente | null>(null);

  const puoModificare = hasPermission("devices:update");
  /**
   * Solo admin, come per l'eliminazione del device. Il backend rifiuta comunque
   * gli altri ruoli: il controllo qui serve a non mostrare un pulsante che
   * risponde sempre "non puoi".
   */
  const puoEliminare = hasPermission("devices:delete");

  /**
   * Estratta dall'effetto perché serve anche dopo un salvataggio: si ricarica
   * tutto invece di aggiornare il record in memoria, dato che la PATCH
   * risponde col solo device e non con le relazioni, gli eventi e i tre
   * elenchi collegati che questa pagina mostra.
   */
  const carica = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const d = await devicesApi.getDevice(id);
      setDevice(d.record);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare il dispositivo."), "error");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    void carica();
  }, [carica]);

  /**
   * Relazioni in corso e relazioni chiuse, separate: sono due cose diverse.
   * Le correnti sono un elenco e non una sola — una cassaforte condivisa serve
   * più clienti contemporaneamente.
   */
  const { correnti, storiche } = useMemo(() => {
    if (!device) return { correnti: [], storiche: [] };
    return {
      correnti: device.clienti.filter((c) => c.data_fine === null),
      storiche: device.clienti.filter((c) => c.data_fine !== null),
    };
  }, [device]);

  /**
   * Il collegamento da mostrare in riepilogo. La regola — e il perché non sia
   * una colonna di `devices` — sta in `collegamento.ts`.
   */
  const collegamento = useMemo(() => (device ? collegamentoCorrente(device.attivazioni) : null), [device]);

  const gruppi = useMemo(() => {
    if (!device) return [];
    const perData = (a: RigaStorico, b: RigaStorico) => {
      if (a.data && b.data) return a.data < b.data ? 1 : -1;
      return a.data ? -1 : b.data ? 1 : 0;
    };

    return [
      {
        chiave: "installazioni" as SectionKey,
        etichetta: "Installazioni",
        icona: HardDrive,
        righe: device.installazioni
          .map<RigaStorico>((r) => ({
            id: r.id,
            titolo: r.insegna ?? "Installazione",
            data: r.data_effettiva ?? r.ipotesi_data,
            // Ha vinto il fallback: è la data ipotizzata, non un fatto. Vale
            // anche se è passata — una previsione mai confermata non diventa
            // un fatto solo perché quel giorno è trascorso.
            dataStimata: !r.data_effettiva,
            daIpotesiData: true,
            stato: r.stato,
            dettaglio: r.localita,
          }))
          .sort(perData),
      },
      {
        chiave: "attivazioni" as SectionKey,
        etichetta: "Attivazioni",
        icona: ShieldCheck,
        righe: device.attivazioni
          .map<RigaStorico>((r) => ({
            id: r.id,
            titolo: r.cliente_nome ?? "Attivazione",
            data: r.data_effettiva ?? r.data_attivazione,
            dataStimata: !r.data_effettiva,
            stato: r.stato,
            // Il collegamento sta anche sulla riga, non solo nel riepilogo: la
            // card dice com'è adesso, l'elenco dice se è cambiato — ed è la
            // domanda che ci si fa davanti a un device riattivato.
            dettaglio: [r.sede, r.comunicazione_tipo].filter(Boolean).join(" · ") || null,
          }))
          .sort(perData),
      },
      {
        chiave: "interventi" as SectionKey,
        etichetta: "Interventi",
        icona: Wrench,
        righe: device.interventi
          .map<RigaStorico>((r) => ({
            id: r.id,
            titolo: r.nome ?? r.cliente_nome ?? "Intervento",
            data: r.data_completamento ?? r.data_prevista,
            dataStimata: !r.data_completamento,
            stato: r.stato,
            dettaglio: r.cliente_nome,
          }))
          .sort(perData),
      },
      {
        chiave: "sopralluoghi" as SectionKey,
        etichetta: "Sopralluoghi",
        icona: ClipboardList,
        // DUE strade in una sezione sola: quelli collegati direttamente al pezzo
        // (rilievi su una cassaforte già installata) e quelli che arrivano
        // attraverso l'installazione, che è il caso normale — il sopralluogo
        // precede il device, quindi non può puntarlo. Per chi guarda lo storico
        // sono la stessa cosa; due elenchi separati direbbero che c'è una
        // differenza che qui non esiste.
        //
        // Deduplicati per id: se un sopralluogo fosse collegato per entrambe le
        // vie comparirebbe due volte, e sembrerebbero due rilievi.
        righe: Object.values(
          [
            ...device.sopralluoghi,
            ...device.installazioni.flatMap((i) => i.sopralluoghi.map((r) => r.sopralluogo)),
          ].reduce<Record<string, RigaStorico>>((acc, r) => {
            acc[r.id] = {
              id: r.id,
              titolo: r.insegna_cliente ?? "Sopralluogo",
              data: r.data_sopralluogo,
              // `data_sopralluogo` è NOT NULL: è sempre un fatto, mai una stima.
              dataStimata: false,
              stato: r.stato,
              dettaglio: null,
            };
            return acc;
          }, {})
        ).sort(perData),
      },
    ];
  }, [device]);

  if (isLoading) return <p className="py-12 text-center text-sm text-ink/50">Caricamento…</p>;

  if (!device) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-ink/60">Dispositivo non trovato.</p>
        <LinkIndietro to="/devices" className="mt-2 inline-block text-sm font-semibold text-primary hover:underline">
          Torna ai Dispositivi
        </LinkIndietro>
      </div>
    );
  }

  const modello = modelloLeggibile(device);
  const centro = centroInstallazione(device);

  return (
    <div>
      {/* Torna all'elenco com'era (filtri, ricerca, scroll) se si arriva da lì;
          altrimenti apre l'elenco. Vedi `LinkIndietro`. */}
      <LinkIndietro to="/devices" className="mb-4 inline-flex items-center gap-1.5 text-sm text-ink/50 hover:text-ink">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Dispositivi
      </LinkIndietro>

      {/* --- Intestazione ------------------------------------------------------ */}
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Vault className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-ink">{etichettaMatricola(device.matricola)}</h1>
              <Badge color={coloreStato(device.stato)}>{STATO_LABELS[device.stato]}</Badge>
            </div>
            <p className="mt-0.5 text-sm text-ink/60">
              {modello ?? "Modello non indicato"}
              {modello && !device.dispositivo_catalogo && (
                <span className="text-ink/40"> — non riconciliato con il Catalogo</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Solo con `report:invia`: il report elenca anche i clienti serviti da
              una cassaforte condivisa, quindi non segue `devices:read`. */}
          {hasPermission("report:invia") && (
            <Button variant="secondary" onClick={() => setReportAperto(true)}>
              <FileBarChart className="h-4 w-4" aria-hidden="true" />
              Report
            </Button>
          )}
          {puoModificare && (
            <Button variant="secondary" onClick={() => setModificaAperta(true)}>
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Modifica
            </Button>
          )}
        </div>
      </header>

      <InviaReportModal
        open={reportAperto}
        onClose={() => setReportAperto(false)}
        titolo={`Report dispositivo — ${etichettaMatricola(device.matricola)}`}
        nomeAllegato={reportApi.nomeFileReport("report-dispositivo", etichettaMatricola(device.matricola))}
        caricaDestinatari={() => reportApi.destinatariPropostiReport("device", device.id)}
        scarica={() =>
          reportApi.scaricaReport("device", device.id, reportApi.nomeFileReport("report-dispositivo", etichettaMatricola(device.matricola)))
        }
        invia={(destinatari, nota) => reportApi.inviaReport("device", device.id, destinatari, nota)}
      />

      {/* --- Riepilogo --------------------------------------------------------- */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* DOVE STA, non "di chi è". Prima questa card mostrava il cliente
            della prima relazione, e su un device condiviso la sede spariva del
            tutto, sostituita da "Condivisa con altri N clienti": proprio nel
            caso in cui sapere dove sta conta di più. Peggio, l'ordinamento
            delle relazioni è `data_inizio desc nulls last` e `data_inizio` è
            nullo su tutte quelle dell'import, quindi QUALE dei clienti
            comparisse era di fatto arbitrario.

            Ora il titolo è la posizione fisica del device, e i clienti — che
            possono essere più d'uno e cambiare senza che il device si sposti —
            stanno nella riga sotto. Sono due fatti diversi e ora si leggono
            come tali. L'elenco completo resta nella sezione "Clienti e sedi". */}
        <div className="rounded-card border border-line bg-white p-4 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">Posizione</p>
          <p className="mt-1 truncate text-xl font-bold text-ink">
            {device.sede_cliente?.nome_sede ?? "—"}
          </p>
          <p className="mt-0.5 truncate text-xs text-ink/45">
            {device.sede_cliente
              ? [device.sede_cliente.citta, clientiInRiga(correnti)].filter(Boolean).join(" · ")
              : correnti.length > 0
                ? `Posizione non indicata · ${clientiInRiga(correnti)}`
                : "Posizione non indicata · nessuna relazione in corso"}
          </p>
        </div>

        <div className="rounded-card border border-line bg-white p-4 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">Installato il</p>
          <p className="mt-1 text-xl font-bold text-ink">{dataIT(device.data_installazione)}</p>
          {/* Tre righe di informazione diverse, tenute distinte: chi ha
              montato il device, se è stata Evo, e da dove lo sappiamo. Un
              "Installazione Evo" scritto quando nessuno l'ha confermato è
              esattamente ciò che il campo nullable serve a evitare. */}
          <p className="mt-0.5 truncate text-xs text-ink/45">
            {centro ? (
              <>
                da <span className="font-semibold text-ink/70">{centro.nome}</span>
                {!centro.daInstallazione && " (indicato a mano)"}
              </>
            ) : device.installato_da_evo === true ? (
              "Installazione Evo"
            ) : device.installato_da_evo === false ? (
              "Non installato da Evo"
            ) : (
              "Installatore non noto"
            )}
          </p>
        </div>

        <div className="rounded-card border border-line bg-white p-4 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">Possesso</p>
          <p className="mt-1 text-xl font-bold text-ink">
            {device.tipo_possesso ? TIPO_POSSESSO_LABELS[device.tipo_possesso] : "—"}
          </p>
          <p className="mt-0.5 text-xs text-ink/45">
            {device.tipo_possesso ? "Da anagrafica" : "Non noto per il parco storico"}
          </p>
        </div>

        {/* Il tipo di collegamento non è una colonna di `devices` e non deve
            diventarlo: si legge dalle attivazioni. La riga sotto il valore dice
            SEMPRE da dove arriva — è la stessa regola delle altre tre card, dove
            "Da anagrafica" e "(indicato a mano)" servono a non far passare per
            certo un dato dedotto.

            La card compare per i tipi che una comunicazione ce l'hanno
            (Cassaforte e Allarme, gli stessi due per cui la sezione esiste nel
            form Attivazioni) OPPURE quando un'attivazione ne indica una
            comunque. Il secondo ramo non è ridondante: `tipo_dispositivo` è
            null per quasi tutto il parco storico, e nasconderla lì
            butterebbe via il dato proprio dove è l'unico che si ha. Su un
            Rendiresto senza attivazioni, invece, sarebbe un trattino che
            occupa un quarto di riepilogo per non dire niente. */}
        {(TIPI_CON_COLLEGAMENTO.includes(device.tipo_dispositivo ?? "") || collegamento) && (
        <div className="rounded-card border border-line bg-white p-4 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">Collegamento</p>
          <p className="mt-1 text-xl font-bold text-ink">{collegamento?.tipo ?? "—"}</p>
          <p className="mt-0.5 truncate text-xs text-ink/45">
            {!collegamento
              ? "Nessuna attivazione lo indica"
              : collegamento.soloCessate
                ? `Ultimo noto${collegamento.data ? `, dall'attivazione del ${dataIT(collegamento.data)}` : ""} — cessata`
                : collegamento.data
                  ? `Dall'attivazione del ${dataIT(collegamento.data)}`
                  : "Dall'attivazione più recente"}
          </p>
          {collegamento?.discordi && (
            <p className="mt-1 text-xs font-semibold text-accent">
              Attivazioni in corso con collegamenti diversi: qui è mostrato quello più recente.
            </p>
          )}
        </div>
        )}
      </div>

      <div className="flex flex-col gap-6">
        {/* --- Relazioni cliente/sede ------------------------------------------ */}
        <section>
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Building2 className="h-4 w-4" aria-hidden="true" />
            </span>
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink/70">Clienti e sedi</h2>
            <span className="text-sm text-ink/40">{device.clienti.length}</span>
          </div>

          {device.clienti.length === 0 ? (
            <p className="rounded-card border border-dashed border-line px-4 py-6 text-center text-sm text-ink/45">
              Nessun cliente collegato a questo dispositivo.
            </p>
          ) : (
            <ul className="overflow-hidden rounded-card border border-line bg-white shadow-card">
              {[...correnti, ...storiche].map((rel) => (
                <li key={rel.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3 last:border-b-0">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">
                      {rel.sede_cliente ? (
                        <Link to={`/sedi/${rel.sede_cliente.id}`} className="hover:underline">
                          {rel.cliente.nome}
                        </Link>
                      ) : (
                        rel.cliente.nome
                      )}
                    </p>
                    <p className="truncate text-xs text-ink/50">
                      {[
                        rel.sede_cliente
                          ? [rel.sede_cliente.nome_sede, rel.sede_cliente.citta].filter(Boolean).join(" · ")
                          : "Sede non collegata",
                        rel.codice_gdo ? `GDO ${rel.codice_gdo}` : null,
                        rel.conto_tecnico ? `Conto tecnico ${rel.conto_tecnico}` : null,
                        rel.tipo_servizio_precredit,
                      ]
                        .filter(Boolean)
                        .join(" — ")}
                    </p>
                    {/* Le relazioni storiche portano una data di fine STIMATA
                        (l'import non aveva quella reale): dirlo qui evita che
                        venga letta come la data vera dello spostamento. */}
                    {rel.note && <p className="mt-0.5 text-xs text-ink/40">{rel.note}</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {rel.data_fine === null ? (
                      <Badge color="success">In corso</Badge>
                    ) : (
                      <Badge color="neutral">Chiusa il {dataIT(rel.data_fine)}</Badge>
                    )}
                    {/* Solo sulle relazioni APERTE: una chiusa non si chiude
                        una seconda volta, e riaprirla e' un'altra operazione —
                        una riga nuova, non `data_fine` da svuotare.

                        Permesso `devices:update` e non `devices:delete`: qui
                        non si cancella niente, si registra che un rapporto e'
                        finito. E' la stessa autorizzazione con cui lo si e'
                        aperto, dal form di Modifica. */}
                    {puoModificare && rel.data_fine === null && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDaTerminare(rel)}
                        aria-label={`Termina rapporto con ${rel.cliente.nome}`}
                      >
                        <CalendarX2 className="h-4 w-4" aria-hidden="true" />
                        Termina rapporto
                      </Button>
                    )}
                    {/* Su TUTTE le righe, aperte e chiuse: una riga inserita
                        per errore va tolta anche quando qualcuno nel frattempo
                        le ha messo una data di fine.

                        Ultima in riga, sola icona e in rosso: e' distruttiva e
                        deve leggersi diversa da "Termina rapporto", che le sta
                        accanto e a colpo d'occhio risolve lo stesso problema. */}
                    {puoEliminare && (
                      <button
                        type="button"
                        onClick={() => setDaEliminare(rel)}
                        title={`Elimina definitivamente la relazione con ${rel.cliente.nome}`}
                        aria-label={`Elimina definitivamente la relazione con ${rel.cliente.nome}`}
                        className="rounded-lg p-1.5 text-ink/35 transition-colors hover:bg-accent/10 hover:text-accent"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* --- Eventi ----------------------------------------------------------- */}
        {device.eventi.length > 0 && (
          <section>
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <History className="h-4 w-4" aria-hidden="true" />
              </span>
              <h2 className="text-sm font-bold uppercase tracking-wide text-ink/70">Eventi</h2>
              <span className="text-sm text-ink/40">{device.eventi.length}</span>
            </div>
            <ul className="overflow-hidden rounded-card border border-line bg-white shadow-card">
              {device.eventi.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3 last:border-b-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink">{e.tipo_evento.replace(/_/g, " ")}</p>
                    {e.note && <p className="truncate text-xs text-ink/50">{e.note}</p>}
                  </div>
                  <span className="shrink-0 text-sm tabular-nums text-ink/60">{dataIT(e.data_evento)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* --- Sostituzioni ----------------------------------------------------- */}
        {(device.sostituito_da.length > 0 || device.sostituisce.length > 0) && (
          <section>
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Repeat className="h-4 w-4" aria-hidden="true" />
              </span>
              <h2 className="text-sm font-bold uppercase tracking-wide text-ink/70">Sostituzioni</h2>
            </div>
            <ul className="overflow-hidden rounded-card border border-line bg-white shadow-card">
              {device.sostituito_da.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-2 border-b border-line px-4 py-3 last:border-b-0">
                  <p className="text-sm text-ink">
                    Sostituito da{" "}
                    <Link to={`/devices/${s.device_nuovo?.id}`} className="font-semibold text-primary hover:underline">
                      {s.device_nuovo && etichettaMatricola(s.device_nuovo.matricola)}
                    </Link>{" "}
                    <span className="text-ink/50">({s.motivo})</span>
                  </p>
                  <span className="text-sm tabular-nums text-ink/60">{dataIT(s.data_sostituzione)}</span>
                </li>
              ))}
              {device.sostituisce.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-2 border-b border-line px-4 py-3 last:border-b-0">
                  <p className="text-sm text-ink">
                    Ha sostituito{" "}
                    <Link to={`/devices/${s.device_vecchio?.id}`} className="font-semibold text-primary hover:underline">
                      {s.device_vecchio && etichettaMatricola(s.device_vecchio.matricola)}
                    </Link>{" "}
                    <span className="text-ink/50">({s.motivo})</span>
                  </p>
                  <span className="text-sm tabular-nums text-ink/60">{dataIT(s.data_sostituzione)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* --- Record operativi collegati --------------------------------------- */}
        {gruppi.map((g) => {
          const colore = SECTION_COLORS[g.chiave].base;
          const Icona = g.icona;
          return (
            <section key={g.chiave}>
              <div className="mb-2 flex items-center gap-2">
                <span
                  className="flex h-7 w-7 items-center justify-center rounded-lg"
                  style={{ backgroundColor: colore, color: SECTION_COLORS[g.chiave].textOn }}
                >
                  <Icona className="h-4 w-4" aria-hidden="true" />
                </span>
                <h2 className="text-sm font-bold uppercase tracking-wide text-ink/70">{g.etichetta}</h2>
                <span className="text-sm text-ink/40">{g.righe.length}</span>
              </div>

              {g.righe.length === 0 ? (
                <p className="rounded-card border border-dashed border-line px-4 py-6 text-center text-sm text-ink/45">
                  Nessun record collegato a questo dispositivo.
                </p>
              ) : (
                <ul className="overflow-hidden rounded-card border border-line bg-white shadow-card">
                  {g.righe.map((r) => (
                    <li
                      key={r.id}
                      className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3 last:border-b-0"
                      style={{ borderLeft: `3px solid ${colore}` }}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">{r.titolo}</p>
                        {r.dettaglio && <p className="truncate text-xs text-ink/50">{r.dettaglio}</p>}
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        {r.stato && <Badge stato={r.stato}>{r.stato}</Badge>}
                        <DataStorico data={r.data} stimata={r.dataStimata} daIpotesiData={r.daIpotesiData} stato={r.stato} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {/* La `key` rimonta il form quando cambiano i dati: react-hook-form
          calcola i defaultValues una volta sola al mount, quindi senza,
          riaprendo dopo un salvataggio si rivedrebbero i valori vecchi. */}
      {puoModificare && modificaAperta && (
        <DeviceModifica
          key={device.updated_at ?? device.id}
          open={modificaAperta}
          onClose={() => setModificaAperta(false)}
          device={device}
          onSaved={() => void carica()}
          // Eliminato: la scheda che si sta guardando non esiste più, quindi si
          // torna all'elenco — indietro, se si arriva da lì, così filtri e
          // ricerca restano quelli. Altrimenti `replace` e non `push`: il tasto
          // indietro riporterebbe su un id che ora dà 404.
          onEliminato={() => torna("/devices", { replace: true })}
        />
      )}

      {/* Montato solo quando serve, e con la `key` sulla relazione: il pannello
          tiene la data in uno stato locale inizializzato a oggi, e riaprendolo
          su un altro cliente senza rimontarlo si porterebbe dietro la data
          digitata la volta prima. */}
      {puoModificare && daTerminare && (
        <TerminaRapporto
          key={daTerminare.id}
          open
          onClose={() => setDaTerminare(null)}
          relazione={daTerminare}
          matricola={device.matricola}
          aperteInTutto={correnti.length}
          onTerminato={() => void carica()}
        />
      )}

      {/* Stessa `key` per lo stesso motivo del pannello sopra: la spunta di
          conferma e' uno stato locale, e riaprendo su un'altra riga senza
          rimontare resterebbe gia' segnata. */}
      {puoEliminare && daEliminare && (
        <EliminaRelazione
          key={daEliminare.id}
          open
          onClose={() => setDaEliminare(null)}
          relazione={daEliminare}
          matricola={device.matricola}
          onEliminata={() => void carica()}
        />
      )}

      {device.note && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-ink/70">Note</h2>
          <p className="whitespace-pre-wrap rounded-card border border-line bg-white p-4 text-sm text-ink/80 shadow-card">
            {device.note}
          </p>
        </section>
      )}
    </div>
  );
}
